import { action, observable, runInAction } from "mobx";
import { ClusteringField, PartitionKey } from "../constants";
import type { TimeZones } from "../constants";
import { entriesToDefinition, fromJSON, toJSON } from "../lib/Converter";
import { Entry, EntryInterface } from "./Entry";
import { Suggestion, SuggestionEntry, SuggestionEntryMap } from "./Suggestion";
import I18n from "lib/I18n";
import { EventMaster } from "lib/Api";
import type { BigqueryLoadBatch } from "lib/Api";
import { call } from "lib/apiHelper";
import { EventMastersApi as Api } from "lib/Api";
import { defer } from "lib/defer";
import { notificationStore } from "lib/store";
import * as JSONbig from "json-bigint";

export type InitialProps = {
  env: string;
  envs: string[];
  titleUuid: string;
  eventMasterId: number;
  applicationId: string;
  eventId: string;
  versionNumber: number;
  definition: string;
  example: string;
  lp1LoadInterval: number;
  userAdmin: boolean;
};

let store: Store | undefined;

export function getStore() {
  if (!store) {
    throw new Error("Store not initialized");
  }
  return store;
}

export function createStore(props: InitialProps) {
  store = new Store(props);
}

class Store {
  app: AppStore;
  list: ListStore;
  env: EnvStore;
  ddl: DdlStore;
  jobStatus: JobStatusStore;
  loadStatus: LoadStatusStore;
  s3: S3Store;
  s3Delete: S3DeleteStore;
  tableStatus: TableStatusStore;

  constructor(props: InitialProps) {
    this.app = new AppStore(this, props);
    this.list = new ListStore(this, props);
    this.env = new EnvStore(this, props);
    this.ddl = new DdlStore(this);
    this.jobStatus = new JobStatusStore(this);
    this.loadStatus = new LoadStatusStore(this);
    this.s3 = new S3Store(this);
    this.s3Delete = new S3DeleteStore(this);
    this.tableStatus = new TableStatusStore(this);
  }
}

class StoreBase {
  protected parent: Store;

  constructor(parent: Store) {
    this.parent = parent;
  }
}

class AppStore extends StoreBase {
  readonly titleUuid: string;
  readonly applicationId: string;
  readonly eventMasterId: number;
  readonly eventId: string;
  readonly example: string;
  readonly lp1LoadInterval: number;
  readonly userAdmin: boolean;
  @observable accessor versionNumber = 0;
  @observable accessor tab = "EntryList";

  constructor(parent: Store, props: InitialProps) {
    super(parent);

    this.titleUuid = props.titleUuid;
    this.applicationId = props.applicationId;
    this.eventMasterId = props.eventMasterId;
    this.eventId = props.eventId;
    this.versionNumber = props.versionNumber;
    this.example = props.example;
    this.lp1LoadInterval = props.lp1LoadInterval;
    this.userAdmin = props.userAdmin;
  }
}

// ref: https://developer.mozilla.org/ja/docs/Web/API/WindowEventHandlers/onbeforeunload
const beforeUnload = (e: BeforeUnloadEvent) => {
  e.preventDefault();
  e.returnValue = I18n.t("event_master_editor.messages.unsaved_unload");
  return I18n.t("event_master_editor.messages.unsaved_unload");
};

export class ListStore extends StoreBase {
  @observable accessor definition = "[]";
  @observable accessor partitionKey = PartitionKey.Unspecified;
  @observable accessor clusteringField = ClusteringField.Unspecified;
  @observable accessor invalidKeyAllowed = false;
  @observable accessor entryList: Entry[] = [];
  @observable accessor isLoading = false;
  @observable accessor isSaved = true;
  @observable accessor versionNote = "";
  @observable accessor isAdvancedOptionsOpen = false;
  @observable accessor json = "";
  @observable accessor jsonError = "";
  @observable accessor suggestion = new Suggestion();

  constructor(parent: Store, props: InitialProps) {
    super(parent);

    const entries: EntryInterface[] = props.definition
      ? JSON.parse(props.definition)
      : [];
    const entryList = entries.map((entry) => new Entry(entry));
    const json = toJSON(entryList);

    this.definition = props.definition;
    this.entryList = entryList;
    this.json = json;
  }

  @action setChanged() {
    window.onbeforeunload = beforeUnload;
    this.isSaved = false;
  }

  @action setSaved() {
    window.onbeforeunload = null;
    this.isSaved = true;
  }

  @action changePartitionKey(partitionKey: PartitionKey) {
    this.partitionKey = partitionKey;
    this.setChanged();
  }

  @action changeClusteringField(clusteringField: ClusteringField) {
    this.clusteringField = clusteringField;
    this.setChanged();
  }

  @action setDefaultAdvancedOptions() {
    this.partitionKey = PartitionKey.NcRecordedAt;
    this.clusteringField = ClusteringField.Unspecified;
    this.setChanged();
  }

  @action addEntry(entry?: Entry) {
    if (!entry) {
      // root level
      this.entryList.push(new Entry());
    } else {
      entry.fields.push(new Entry());
    }
    this.validateEntries();
    this.exportJson();
    this.setChanged();
  }

  @action removeEntry(entry: Entry | undefined, index: number) {
    if (!entry) {
      if (index >= this.entryList.length) {
        return;
      }
      this.entryList.splice(index, 1);
    } else {
      if (index >= entry.fields.length) {
        return;
      }
      entry.fields.splice(index, 1);
    }
    this.validateEntries();
    this.exportJson();
    this.setChanged();
  }

  @action updateEntry<T extends keyof EntryInterface>(
    entry: Entry,
    target: T,
    value: EntryInterface[T],
  ) {
    // @ts-expect-error TS2322
    entry[target] = value;
    this.validateEntries();
    this.exportJson();
    this.setChanged();
  }

  @action validateEntries() {
    for (const entry of this.entryList) {
      entry.validate(this.entryList, this.invalidKeyAllowed, 0);
    }
  }

  @action exportJson() {
    this.json = toJSON(this.entryList);
  }

  @action importJson(json: string) {
    try {
      const nextEntries = fromJSON(json);
      this.json = toJSON(nextEntries);
      this.jsonError = "";
      this.entryList = nextEntries.map((entry) => new Entry(entry));
      this.validateEntries();
      this.setChanged();
    } catch (e) {
      this.json = json;
      this.jsonError =
        I18n.t("event_master_editor.errors.invalid_json") +
        (e as Error).message;
    }
  }

  @action updateDefinition(
    definition: EventMaster["definition"],
    partitionKey: PartitionKey,
    clusteringField: ClusteringField,
    invalidKeyAllowed: boolean,
  ) {
    const entryList = (definition as EntryInterface[]).map(
      (entry) => new Entry(entry),
    );
    this.entryList = entryList;
    this.partitionKey = partitionKey;
    this.clusteringField = clusteringField;
    this.invalidKeyAllowed = invalidKeyAllowed;
    this.definition = JSON.stringify(definition);
    this.setChanged();
  }

  public hasError(entries?: Entry[]) {
    for (const entry of entries ?? this.entryList) {
      if (
        (entry.error.messages.get("key") ?? []).some(
          (message) => message.level == "error",
        )
      ) {
        return true;
      }
      if ((entry.error.messages.get("type") ?? []).length > 0) {
        return true;
      }
      if (entry.type == "RECORD" && this.hasError(entry.fields)) {
        return true;
      }
    }
    return false;
  }

  @action updateSuggestion(suggestionString: string) {
    const suggestions: SuggestionEntryMap = JSON.parse(suggestionString);
    this.suggestion.isLoading = false;
    this.suggestion.suggestions = this.filterSuggestions(
      this.entryList,
      this.convertSuggestionEntryMap(suggestions),
    );
  }

  convertSuggestionEntryMap(
    suggestionMap: SuggestionEntryMap,
  ): SuggestionEntry[] {
    const ret: SuggestionEntry[] = [];
    const keys = Object.keys(suggestionMap);
    for (const key of keys) {
      const value = suggestionMap[key];
      const suggestion: SuggestionEntry = {
        key: key,
        type: value.type,
        repeated: value.repeated,
        fields: [],
      };
      if (value.type == "RECORD") {
        suggestion.fields = this.convertSuggestionEntryMap(value.fields);
      }
      ret.push(suggestion);
    }
    return ret;
  }

  filterSuggestions(
    entries: Entry[],
    suggestions: SuggestionEntry[],
  ): SuggestionEntry[] {
    const ret: SuggestionEntry[] = [];
    for (const suggestion of suggestions) {
      const existEntry = entries.find((entry) => entry.key == suggestion.key);
      if (existEntry) {
        if (existEntry.type == "RECORD" && suggestion.type == "RECORD") {
          const newSuggestion = this.filterSuggestions(
            existEntry.fields,
            suggestion.fields,
          );
          if (newSuggestion.length > 0) {
            ret.push({ ...suggestion, fields: newSuggestion });
          }
        }
        continue;
      }
      ret.push(suggestion);
    }
    return ret;
  }

  @action applySuggestion() {
    this.suggestion.isOpened = false;
    this.applySuggestionImpl(this.entryList, this.suggestion.suggestions);
    this.validateEntries();
    this.setChanged();
  }

  applySuggestionImpl(entries: Entry[], suggestions: SuggestionEntry[]) {
    for (const suggestion of suggestions) {
      const newEntry = new Entry(suggestion);
      const existEntry = entries.find((entry) => entry.key == newEntry.key);
      if (
        existEntry &&
        existEntry.type == "RECORD" &&
        newEntry.type == "RECORD"
      ) {
        this.applySuggestionImpl(existEntry.fields, suggestion.fields);
        // merge
        continue;
      }
      if (!existEntry) {
        entries.push(newEntry);
      }
    }
  }

  @action async reloadDefinition() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.showEventMaster(this.parent.app.eventMasterId, {
        versionNumber: this.parent.app.versionNumber,
      }),
    );
    if (resp.code != 200) {
      return;
    }
    this.updateDefinition(
      resp.body.definition,
      resp.body.partitionKey as PartitionKey,
      resp.body.clusteringFields as ClusteringField,
      resp.body.invalidKeyAllowed,
    );
    this.validateEntries();
    this.exportJson();
    this.setSaved();
  }

  @action async getSuggestion() {
    this.suggestion.isLoading = true;
    using _ = defer(() => (this.suggestion.isLoading = false));
    const resp = await call(
      Api.showEventMastersSuggestion(this.parent.app.eventMasterId, {
        limit: this.suggestion.limit.toString(),
        env: this.suggestion.env,
      }),
    );
    if (resp.code != 200) {
      return;
    }
    this.updateSuggestion(resp.body);
  }

  @action async postDefinition() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.updateEventMaster(this.parent.app.eventMasterId, {
        eventMaster: {
          definition: entriesToDefinition(this.entryList),
          partitionKey: this.partitionKey,
          clusteringFields: this.clusteringField,
          versionNote: this.versionNote,
        },
      }),
    );
    if (resp.code != 200) {
      return;
    }
    this.parent.app.versionNumber = resp.body.versionNumber;
    this.versionNote = "";
    notificationStore.notifySuccess(resp.body.message);
    this.setSaved();
  }
}

class EnvStore extends StoreBase {
  readonly list: string[];
  @observable accessor selected = "";

  constructor(parent: Store, props: InitialProps) {
    super(parent);

    this.selected = props.env;
    this.list = props.envs;
  }

  isLp1() {
    return this.selected === "lp1";
  }
}

class DdlStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor current = "";
  @observable accessor next = "";
  @observable accessor migration = "";

  @action async getDdl() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.ddlEventMastersBigquery(
        this.parent.app.eventMasterId,
        this.parent.env.selected,
        {
          versionNumber: this.parent.app.versionNumber,
        },
      ),
    );
    if (resp.code != 200) {
      return;
    }
    this.current = resp.body.current;
    this.next = resp.body.next;
    this.migration = resp.body.migration;
  }
}

interface DeployStatus {
  isLoading: boolean;
  value: string;
}

interface CopyStatus {
  isLoading: boolean;
  value: string;
  jobId?: number;
}

class JobStatusStore extends StoreBase {
  @observable accessor deploy: DeployStatus = { isLoading: false, value: "" };
  @observable accessor copy: CopyStatus = { isLoading: false, value: "" };

  @action async getDeployStatus() {
    this.deploy.isLoading = true;
    using _ = defer(() => (this.deploy.isLoading = false));
    const resp = await call(
      Api.lastDeployStatusEventMastersJob(this.parent.app.eventMasterId),
    );
    if (resp.code != 200) {
      return;
    }
    this.deploy.value = resp.body.status;
  }

  @action async getCopyStatus() {
    this.copy.isLoading = true;
    using _ = defer(() => (this.copy.isLoading = false));
    const resp = await call(
      Api.lastCopyStatusEventMastersJob(this.parent.app.eventMasterId),
    );
    if (resp.code != 200) {
      return;
    }
    this.copy.value = resp.body.status;
    this.copy.jobId = resp.body.jobId;
  }

  @action async postDeploy() {
    this.deploy.isLoading = true;
    using _ = defer(() => (this.deploy.isLoading = false));
    const resp = await call(
      Api.deployEventMasterEventMastersBigquery(
        this.parent.app.eventMasterId,
        this.parent.env.selected,
        { versionNumber: this.parent.app.versionNumber },
      ),
    );
    if (resp.code != 200) {
      return;
    }
    notificationStore.notifySuccess(resp.body.message);
  }
}

export interface TimeKey {
  time: string;
}

class LoadStatusStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor deployed = false;
  @observable accessor enabled = false;
  @observable accessor running = false;
  @observable accessor currentJobId = "";
  @observable accessor currentTimeKey: TimeKey | undefined = undefined;
  @observable accessor currentStartedAt = "";
  @observable accessor lastJobId = "";
  @observable accessor lastResult = "";
  @observable accessor lastTimeKey: TimeKey | undefined = undefined;
  @observable accessor lastStartedAt = "";
  @observable accessor lastFinishedAt = "";
  @observable accessor lastError = "";
  @observable accessor currentBatch: BigqueryLoadBatch | undefined = undefined;

  @action async getLoadStatus() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.showEventMastersBigqueryLoadStatus(
        this.parent.app.eventMasterId,
        this.parent.env.selected,
      ),
    );
    if (resp.code != 200) {
      return;
    }
    runInAction(() => {
      this.deployed = resp.body.deployed;
      this.enabled = resp.body.enabled;
      this.running = resp.body.running;
      this.currentJobId = resp.body.currentJobId;
      this.currentTimeKey = resp.body.currentTimeKey;
      this.currentStartedAt = resp.body.currentStartedAt;
      this.lastJobId = resp.body.lastJobId;
      this.lastResult = resp.body.lastResult;
      this.lastTimeKey = resp.body.lastTimeKey;
      this.lastStartedAt = resp.body.lastStartedAt;
      this.lastFinishedAt = resp.body.lastFinishedAt;
      this.lastError = resp.body.lastError;
      this.currentBatch = resp.body.currentBatch;
    });
  }
}

export interface S3Stats {
  datetime: string;
  term: string;
  fileCount: number | undefined;
  totalSize: number | undefined;
}

class S3Store extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor s3StatsList: S3Stats[] = [];
  @observable accessor timeFrom = "";
  @observable accessor timeTo = "";
  @observable accessor timeZone: TimeZones = "JST";

  @action async getS3Stats() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.s3StatsEventMastersBigquery(
        this.parent.app.eventMasterId,
        this.parent.env.selected,
        {
          versionNumber: this.parent.app.versionNumber,
          timeZone: this.timeZone,
          timeFrom: this.timeFrom,
          timeTo: this.timeTo,
        },
      ),
    );
    if (resp.code != 200) {
      return;
    }
    this.s3StatsList = resp.body;
  }

  @action async copyFromS3() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.copyFromS3EventMastersBigquery(
        this.parent.app.eventMasterId,
        this.parent.env.selected,
        {
          versionNumber: this.parent.app.versionNumber,
          timeZone: this.timeZone,
          timeFrom: this.timeFrom,
          timeTo: this.timeTo,
        },
      ),
    );
    if (resp.code != 200) {
      return;
    }
    notificationStore.notifySuccess(resp.body.message);
  }
}

class S3DeleteStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor timeFrom: string = "";
  @observable accessor timeTo: string = "";
  @observable accessor timeZone: TimeZones = "JST";

  @action async deleteFromS3() {
    if (!this.timeFrom || !this.timeTo) {
      return;
    }
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.deleteEventMastersS3(
        this.parent.app.eventMasterId,
        this.parent.env.selected,
        {
          timeZone: this.timeZone,
          timeFrom: this.timeFrom,
          timeTo: this.timeTo,
        },
      ),
    );
    if (resp.code != 200) {
      return;
    }
    notificationStore.notifySuccess(resp.body.message);
  }
}

class TableStatusStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor rowCount = 0;
  @observable accessor columns: string[] = [];
  @observable accessor rows: { [index: string]: string }[] = [];

  @action async getTableStatus() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const resp = await call(
      Api.tableStatusEventMastersBigquery(
        this.parent.app.eventMasterId,
        this.parent.env.selected,
        {
          versionNumber: this.parent.app.versionNumber,
        },
      ),
    );
    if (resp.code != 200) {
      return;
    }
    const rows = JSONbig.parse(resp.body.rows);
    runInAction(() => {
      this.rows = rows;
      this.rowCount = resp.body.rowCount;
      this.columns = resp.body.columns;
    });
  }
}
